/**
 * PriceBox - Componente personalizado para mostrar precios de productos
 * Maneja diferentes formatos de precios (con/sin descuento, con/sin IVA)
 * y muestra el precio por unidad cuando corresponde
 */
class PriceBox extends HTMLElement {
  
  constructor() {
    super();

    // Inicialización de propiedades principales
    this.data = null;
    this.originalPrice = null;
    this.finalPrice = null;
    this.compareAtPrice = null;
    this.iva = null;
  }

  connectedCallback() {
    // Obtiene referencias a los contenedores de precios en el DOM
    this.containerOriginalPrice = this.querySelector('[data-original-price]');
    this.containerFinalPrice = this.querySelector('[data-final-price]');
    this.containerCompareAtPrice = this.querySelector('[data-cp-at-price]');
    this.containerUnitPerType = this.querySelector('[data-unit-type]');
    this.containerPriceUnitPerType = this.querySelector('[data-price-unit-type]');

    this.init();

    // Escucha cambios en el tipo de cliente (B2B/B2C) para actualizar precios
    document.addEventListener('biribox:customer-type-changed', () => {
      this.setVariables();
      this.render();
    });
  }

  setVariables(){
    // Determina si mostrar precios con o sin IVA según el tipo de cliente
    const customerType = localStorage.getItem('ls.biribox.view-type');

    // Extrae los datos de precios del JSON y proporciona valores predeterminados
    this.originalPrice = this.data.original_price || 0;
    this.finalPrice = this.data.final_price || 0;
    this.compareAtPrice = this.data.compare_at_price || 0;
    this.iva = this.data.iva || 0;
    this.unitPerType = parseInt(this.data.unit_per_type) || 0;  // Convierte a número entero

    // Para clientes particulares (B2C), añade el IVA a los precios
    if(customerType === 'private') {
      this.originalPrice = this.originalPrice * (this.iva + 100) / 100;
      this.finalPrice = this.finalPrice * (this.iva + 100) / 100;
      this.compareAtPrice = this.compareAtPrice * (this.iva + 100) / 100;
    }
  }

  init() {
    // Obtiene y parsea los datos de precios del JSON incrustado en el HTML
    this.data = this.querySelector('#data-price-box').textContent;
    this.data = JSON.parse(this.data);

    this.setVariables();
    this.render();
  }

  render() {
    // Lógica para mostrar diferentes tipos de precios según el contexto
    if(this.data.type === 'line-item' || this.data.type === 'card-product' || this.data.type === 'final-price') {
      if(this.originalPrice != this.finalPrice) {
        // Muestra precio con descuento cuando el precio original y final difieren
        this.containerOriginalPrice.textContent = this.formatCentsToEuro(this.originalPrice);
        this.containerFinalPrice.textContent = this.formatCentsToEuro(this.finalPrice);
        this.showPriceWithDiscount();
      }else if(this.originalPrice < this.compareAtPrice){
        // Muestra precio comparativo cuando hay un precio de comparación mayor
        this.containerCompareAtPrice.textContent = this.formatCentsToEuro(this.compareAtPrice);
        this.containerFinalPrice.textContent = this.formatCentsToEuro(this.finalPrice);
        this.showCompareAtPrice();
      }else{
        // Muestra solo el precio final cuando no hay descuentos ni comparativos
        this.containerFinalPrice.textContent = this.formatCentsToEuro(this.finalPrice);
        this.showFinalPrice();
      }
    }

    // Para tarjetas de producto, muestra también el precio por unidad
    if(this.data.type != 'final-price'){
      this.showUnitPerType();
    }
  }

  resetClass() {
    // Restablece las clases CSS para ocultar todos los contenedores de precios
    this.containerOriginalPrice.classList.add('hidden');
    this.containerFinalPrice.classList.add('hidden');
    this.containerCompareAtPrice.classList.add('hidden');
    this.containerFinalPrice.classList.remove('c-cp__price__pricebox--sale');
  }

  showCompareAtPrice() {
    // Muestra el precio de comparación y el precio final
    const dataUnitType = this.querySelector('[data-unit-type]');

    this.resetClass();
    this.containerCompareAtPrice.classList.remove('hidden');
    this.containerFinalPrice.classList.add('c-cp__price__pricebox--sale');
    this.containerFinalPrice.classList.remove('hidden');
    
    // Aplica estilo al tipo de unidad si existe
    if(dataUnitType){
      dataUnitType.classList.add('c-cp__price__pricebox--sale');
    }
  }

  showPriceWithDiscount() {
    // Muestra el precio original tachado y el precio final con descuento
    const dataUnitType = this.querySelector('[data-unit-type]');

    this.resetClass();
    this.containerOriginalPrice.classList.remove('hidden');
    this.containerFinalPrice.classList.add('c-cp__price__pricebox--sale');
    this.containerFinalPrice.classList.remove('hidden');

    if(dataUnitType){
      dataUnitType.classList.add('c-cp__price__pricebox--sale');
    }
  }

  showUnitPerType(){
    // Muestra el precio por unidad cuando hay múltiples unidades por tipo
    this.containerUnitPerType?.classList.remove('hidden');
    if(this.unitPerType > 1){
      // Calcula y muestra el precio por unidad individual
      const spanElement = this.containerPriceUnitPerType?.querySelector('span');
      if (spanElement) {
        spanElement.textContent = this.formatCentsToEuro((this.finalPrice / this.unitPerType));
      }
      this.containerPriceUnitPerType?.classList.remove('hidden');
    }
  }

  showFinalPrice() {
    // Muestra solo el precio final sin descuentos ni comparativos
    this.resetClass();
    this.containerFinalPrice.classList.remove('hidden');
  }

  /**
   * Convierte un valor en centavos a formato europeo: "21,82 €"
   * @param {number|string} cents - Valor en centavos (p. ej. 2182)
   * @returns {string} Precio formateado ("21,82 €")
   */
  formatCentsToEuro(cents) {
    const value = Number(cents);

    if (isNaN(value)) return "0,00€";

    // Convertir a euros dividiendo entre 100
    const euros = value / 100;

    // Formato europeo sin decimales raros
    return euros
      .toFixed(2)                // "21.82"
      .replace('.', ',') + '€'; // "21,82 €"
  }
}

// Registra el componente personalizado para su uso en HTML
customElements.define('price-box', PriceBox);